<p-dialog
  [(visible)]="visible"
  [modal]="true"
  [style]="{ width: '55rem' }"
  [header]="
    viewMode
      ? ('goods.classify.dialog.property.view_title' | translate)
      : ('goods.classify.dialog.property.add_title' | translate)
  "
  (onHide)="onCancel()"
>
  <div class="px-4 min-h-[350px]" [class.pointer-events-none]="viewMode">
    <!-- Basic Information Section -->
    <h2 class="font-medium text-lg mb-3">
      {{ "goods.classify.dialog.property.basic_information" | translate }}
    </h2>
    <form [formGroup]="propertyForm" class="grid grid-cols-2 gap-4 items-start">
      <!-- Property Name -->
      <div>
        <label
          for="property-name"
          class="block text-sm font-medium text-gray-700 mb-1"
        >
          {{ "goods.classify.dialog.property.property_name" | translate }}
          <span class="text-red-500">*</span>
        </label>
        <input
          pInputText
          id="property-name"
          formControlName="name"
          class="w-full"
          [placeholder]="
            'goods.classify.dialog.property.enter_property_name' | translate
          "
          [disabled]="viewMode"
        />
        @if (
          propertyForm.get("name")?.errors?.['required'] && propertyForm.get("name")?.touched
        ) {
          <small class="block mt-1 text-xs text-red-500">
            {{
              "goods.classify.dialog.property.validation.property_name_required"
                | translate
            }}
          </small>
        }
        @if (propertyForm.get("name")?.errors?.['maxlength']) {
          <small class="block mt-1 text-xs text-red-500">
            {{ 'common.validation.maxlength' | translate: maxLengthParams('goods.classify.dialog.property.property_name', 255) }}
          </small>
        }
      </div>

      <!-- Property Code -->
      <div>
        <label
          for="property-code"
          class="block text-sm font-medium text-gray-700 mb-1"
        >
          {{ "goods.classify.dialog.property.property_code" | translate }}
          <span class="text-red-500">*</span>
        </label>
        <input
          pInputText
          id="property-code"
          formControlName="code"
          class="w-full"
          [placeholder]="
            'goods.classify.dialog.property.enter_property_code' | translate
          "
          [disabled]="viewMode"
        />
        @if (
          propertyForm.get("code")?.errors?.['required'] && propertyForm.get("code")?.touched
        ) {
          <small class="block mt-1 text-xs text-red-500">
            {{
              "goods.classify.dialog.property.validation.property_code_required"
                | translate
            }}
          </small>
        }
        @if (propertyForm.get("code")?.errors?.['maxlength']) {
          <small class="block mt-1 text-xs text-red-500">
            {{ 'common.validation.maxlength' | translate: maxLengthParams('goods.classify.dialog.property.property_code', 255) }}
          </small>
        }
      </div>

      <!-- Display Type -->
      <div>
        <label
          for="property-form-type"
          class="block text-sm font-medium text-gray-700 mb-1"
        >
          {{ "goods.classify.dialog.property.display_type" | translate }}
          <span class="text-red-500">*</span>
        </label>
        <p-select
          formControlName="formType"
          inputId="property-form-type"
          [options]="formTypeOptions"
          optionLabel="label"
          optionValue="value"
          [placeholder]="
            'goods.classify.dialog.property.select_display_type' | translate
          "
          class="w-full"
          appendTo="body"
          (onChange)="onFormTypeChange()"
          [disabled]="viewMode"
        ></p-select>
        @if (
          propertyForm.get("formType")?.invalid &&
          propertyForm.get("formType")?.touched
        ) {
          <small class="block mt-1 text-xs text-red-500">
            {{
              "goods.classify.dialog.property.validation.display_type_required"
                | translate
            }}
          </small>
        }
        <small class="text-gray-500 text-xs mt-1 block">
          {{ "goods.classify.dialog.property.display_type_help" | translate }}
        </small>
      </div>

      <!-- Remarks -->
      <div class="form-field">
        <label
          for="property-remark"
          class="block text-sm font-medium text-gray-700 mb-1"
        >
          {{ "goods.classify.dialog.property.remarks" | translate }}
        </label>
        <input
          pInputText
          id="property-remark"
          formControlName="remark"
          class="w-full text-sm"
          [placeholder]="
            'goods.classify.dialog.property.enter_remarks' | translate
          "
          [disabled]="viewMode"
        />
        @if (propertyForm.get("remark")?.errors?.['maxlength']) {
          <small class="block mt-1 text-xs text-red-500">
            {{ 'common.validation.maxlength' | translate: maxLengthParams('goods.classify.dialog.property.remarks', 255) }}
          </small>
        }
      </div>

      <!-- Display group (product specs section) -->
      <div>
        <label
          for="property-display-group"
          class="block text-sm font-medium text-gray-700 mb-1"
        >
          {{ "goods.classify.dialog.property.display_group" | translate }}
        </label>
        <p-select
          formControlName="displayGroup"
          inputId="property-display-group"
          [options]="displayGroupOptions"
          optionLabel="label"
          optionValue="value"
          [placeholder]="
            'goods.classify.dialog.property.select_display_group' | translate
          "
          class="w-full"
          appendTo="body"
          [disabled]="viewMode"
        ></p-select>
        <small class="text-gray-500 text-xs mt-1 block">
          {{ "goods.classify.dialog.property.display_group_help" | translate }}
        </small>
      </div>

      <!-- Generate SKU -->
      <div class="flex items-center col-span-2">
        <p-checkbox
          formControlName="skuFlag"
          inputId="property-sku-flag"
          [binary]="true"
          [disabled]="viewMode"
        ></p-checkbox>
        <label
          for="property-sku-flag"
          class="ml-2 text-sm font-medium text-gray-700"
          >{{
            "goods.classify.dialog.property.generate_sku" | translate
          }}</label
        >
      </div>
    </form>

    <!-- Information Content Section (Conditional) -->
    @if (showValueTable()) {
      <p-divider class="mt-4">
        <span class="font-medium"
          >{{
            "goods.classify.dialog.property.information_content" | translate
          }}
          <span class="text-red-500">*</span></span
        >
      </p-divider>

      <div class="mt-2">
        @if (!viewMode) {
          <div class="flex gap-2 mb-4">
            <p-button
              [label]="'common.actions.delete' | translate"
              size="small"
              severity="danger"
              [outlined]="true"
              [disabled]="selectedValues().length === 0"
              (onClick)="onDeleteSelectedValues()"
              class="table-button"
            ></p-button>
            <p-button
              [label]="'common.actions.add' | translate"
              size="small"
              severity="info"
              [outlined]="true"
              (onClick)="onAddValue()"
              class="table-button"
            ></p-button>
          </div>
        }
        <!-- Validation error message -->
        @if (validationError()) {
          <div class="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <p class="text-sm text-red-600">
              <i class="pi pi-exclamation-triangle mr-2"></i>
              {{ validationError() }}
            </p>
          </div>
        }

        <app-data-table
          [dataSource]="propertyValues()"
          [columns]="valueTableColumns"
          [rows]="10"
          [first]="0"
          [totalRecords]="propertyValues().length"
          [loading]="false"
          [selection]="selectedValues()"
          [hasCheckColumn]="!viewMode"
          (selectionChange)="onValueSelectionChange($event)"
          [rowsPerPageOptions]="[5, 10, 20, 50]"
          [scrollHeight]="'270px'"
          [style]="{ 'overflow-x': 'hidden' }"
        >
          <!-- Custom Property Value Template -->
          <ng-template #customTemplate let-value let-rowData="rowData">
            @if (rowData.isEditing && !viewMode) {
              <input
                pInputText
                [value]="rowData.value"
                (input)="onInputChange(rowData, $event)"
                class="w-full p-1 border border-gray-300 rounded text-xs"
                [placeholder]="
                  'goods.classify.dialog.property.enter_property_value'
                    | translate
                "
                #inputRef
              />
            } @else {
              <span class="text-xs">{{ rowData.value }}</span>
            }
          </ng-template>

          <!-- Custom Actions Template -->
          <ng-template #actionsTemplate let-rowData>
            @if (!viewMode) {
              <div class="actions-container">
                @if (rowData.isEditing) {
                  <app-button
                    class="action-save"
                    [label]="'common.actions.save' | translate"
                    type="action"
                    (emitOnClick)="onSaveValue(rowData.id)"
                  ></app-button>
                  <app-button
                    class="action-cancel"
                    [label]="'common.actions.cancel' | translate"
                    type="action"
                    (emitOnClick)="onCancelEdit(rowData.id)"
                  ></app-button>
                } @else {
                  <app-button
                    class="action-edit"
                    [label]="'common.actions.edit' | translate"
                    type="action"
                    (emitOnClick)="onEditValue(rowData.id)"
                  ></app-button>
                  <app-button
                    class="action-delete"
                    [label]="'common.actions.delete' | translate"
                    type="action"
                    (emitOnClick)="onDeleteValue(rowData.id)"
                  ></app-button>
                }
              </div>
            }
          </ng-template>
        </app-data-table>
      </div>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2 mt-4 dialog-footer-actions">
      @if (!viewMode) {
        <app-button
          [label]="'common.actions.cancel' | translate"
          type="secondary"
          [outlined]="true"
          (emitOnClick)="onCancel()"
        ></app-button>
        <app-button
          [label]="'common.actions.confirm' | translate"
          type="info"
          [disabled]="propertyForm.invalid"
          (emitOnClick)="onConfirm()"
        ></app-button>
      } @else {
        <app-button
          [label]="'common.actions.close' | translate"
          type="secondary"
          [outlined]="true"
          (emitOnClick)="onCancel()"
        ></app-button>
      }
    </div>
  </ng-template>
</p-dialog>

<!-- Custom Confirmation Dialog -->
<app-confirmation-dialog
  [(visible)]="showConfirmationDialog"
  [message]="confirmationMessage()"
  [buttons]="confirmationButtons()"
  (emitOnClick)="onConfirmationDialogAction($event)"
></app-confirmation-dialog>
